import { Typography } from '@material-tailwind/react';
import { useEffect, useRef, useState } from 'react';
import {
  FreeTrialContextItem,
  LAST_LESSON_STEP,
  LESSON_SCENE_MAP,
  LESSON_STEPS,
  ReadRepeatPhase,
  STORYTELLING_HINT_POINTS,
} from '../../constants/types/free-trial/type';
import { useAppDispatch } from '../../store/hook';
import { openAlert } from '../../store/features/modalSlice';
import { getErrorMessages } from '../../api/utils/util-func';
import { reportFreeTrialStepProgress } from '../../api/utils/free-trial/api';
import FreeTrialLessonContent from './FreeTrialLessonContent';
import FreeTrialLessonGuide from './FreeTrialLessonGuide';
import { useFreeTrialLessonAudio } from '../../hooks/free-trial/useFreeTrialLessonAudio';
import FreeTrialAttentionEffect from './FreeTrialAttentionEffect';

interface FreeTrialLessonViewProps {
  freeTrialContext: FreeTrialContextItem;
  onShowTutorial: () => void;
  onComplete: () => void;
}

/* 수업하기 */
const FreeTrialLessonView = ({
  freeTrialContext,
  onShowTutorial,
  onComplete,
}: FreeTrialLessonViewProps) => {
  const dispatch = useAppDispatch();

  const [lessonStep, setLessonStep] = useState(0);
  const [sceneIndex, setSceneIndex] = useState(0);
  const [maxUnlockedStep, setMaxUnlockedStep] = useState(() =>
    Boolean(freeTrialContext.stepProgress?.lesson) ? LAST_LESSON_STEP : 0,
  );
  const [isReporting, setIsReporting] = useState(false);

  const [activityProgress, setActivityProgress] = useState(0);
  const [isActivityCompleted, setIsActivityCompleted] = useState(false);
  const [activityResetKey, setActivityResetKey] = useState(0);
  const [isAudioProgressCompleted, setIsAudioProgressCompleted] =
    useState(false);
  const [meaningStep, setMeaningStep] = useState(0);

  const [pointRepeatStep, setPointRepeatStep] = useState(0);
  const [pointRepeatIndex, setPointRepeatIndex] = useState(0);
  const [pointRepeatPartIndex, setPointRepeatPartIndex] = useState(0);

  const [readRepeatIndex, setReadRepeatIndex] = useState(0);
  const [readRepeatPhase, setReadRepeatPhase] =
    useState<ReadRepeatPhase>('LISTEN');

  const [listenRepeatIndex, setListenRepeatIndex] = useState(0);
  const [listenRepeatPhase, setListenRepeatPhase] =
    useState<ReadRepeatPhase>('LISTEN');

  const [isStoryHintVisible, setIsStoryHintVisible] = useState(false);
  const [storyHintIndex, setStoryHintIndex] = useState(0);

  const [isLessonCompleted, setIsLessonCompleted] = useState(false);

  const [isSceneStarted, setIsSceneStarted] = useState(true);

  const sceneStartTimerRef = useRef<number | null>(null);

  const [isPointRepeatPhaseStarted, setIsPointRepeatPhaseStarted] =
    useState(false);

  const pointRepeatPhaseTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (Boolean(freeTrialContext.stepProgress?.lesson)) {
      setMaxUnlockedStep(LAST_LESSON_STEP);
    }
  }, [freeTrialContext]);

  const {
    stopAudio,
    playAudio,
    playSceneAudio,
    audioProgress,
    isAudioPlaying,
  } = useFreeTrialLessonAudio();

  const clearSceneStartTimer = () => {
    if (sceneStartTimerRef.current === null) return;

    window.clearTimeout(sceneStartTimerRef.current);
    sceneStartTimerRef.current = null;
  };

  const clearPointRepeatPhaseTimer = () => {
    if (pointRepeatPhaseTimerRef.current === null) return;

    window.clearTimeout(pointRepeatPhaseTimerRef.current);
    pointRepeatPhaseTimerRef.current = null;
  };

  const startPointRepeatPhase = (
    startStep: 0 | 4,
    step: number,
    index: number,
  ) => {
    clearPointRepeatPhaseTimer();
    stopAudio();

    const scene = LESSON_SCENE_MAP[step]?.[index];

    if (
      !scene ||
      scene.type !== 'SENTENCE' ||
      scene.activity !== 'POINT_REPEAT'
    ) {
      return;
    }

    setIsAudioProgressCompleted(false);
    setActivityProgress(0);
    setIsActivityCompleted(false);

    setIsPointRepeatPhaseStarted(false);
    setPointRepeatStep(startStep);

    if (startStep === 0 || startStep === 4) {
      setPointRepeatPartIndex(0);
      setPointRepeatIndex(0);
    }

    pointRepeatPhaseTimerRef.current = window.setTimeout(() => {
      setIsAudioProgressCompleted(false);
      setIsPointRepeatPhaseStarted(true);

      if (startStep === 0) {
        const firstPoint = scene.pointRepeatParts?.[0]?.points[0];

        if (firstPoint) {
          playAudio(firstPoint.audio);
        }
      }

      if (startStep === 4) {
        const firstPart = scene.pointRepeatParts?.[0];

        if (firstPart) {
          playAudio(firstPart.fullAudio);
        }
      }

      pointRepeatPhaseTimerRef.current = null;
    }, 1000);
  };

  const startScene = (step: number, index: number) => {
    clearSceneStartTimer();
    clearPointRepeatPhaseTimer();
    stopAudio();

    const scene = LESSON_SCENE_MAP[step]?.[index];

    if (!scene) return;

    if (scene.type === 'SENTENCE' && scene.activity === 'POINT_REPEAT') {
      setIsSceneStarted(true);
      startPointRepeatPhase(0, step, index);
      return;
    }

    if (scene.type === 'SENTENCE' && scene.activity === 'READ_REPEAT') {
      setIsSceneStarted(false);

      setReadRepeatIndex(0);
      setReadRepeatPhase('LISTEN');
      setActivityProgress(0);
      setIsActivityCompleted(false);
      setIsAudioProgressCompleted(false);

      sceneStartTimerRef.current = window.setTimeout(() => {
        setIsSceneStarted(true);

        const firstAudio = scene.audios?.[0];

        if (firstAudio) {
          playAudio(firstAudio);
        }

        sceneStartTimerRef.current = null;
      }, 1000);

      return;
    }

    if (scene.type === 'SENTENCE' && scene.activity === 'LISTEN_REPEAT') {
      setIsSceneStarted(false);

      setListenRepeatIndex(0);
      setListenRepeatPhase('LISTEN');

      setActivityProgress(0);
      setIsActivityCompleted(false);
      setIsAudioProgressCompleted(false);

      sceneStartTimerRef.current = window.setTimeout(() => {
        setIsSceneStarted(true);

        const firstAudio = scene.audios?.[0];

        if (firstAudio) {
          playAudio(firstAudio);
        }

        sceneStartTimerRef.current = null;
      }, 1000);

      return;
    }

    if (scene.type === 'STORY' && scene.startDelayMs) {
      setIsSceneStarted(false);
      setIsAudioProgressCompleted(false);

      sceneStartTimerRef.current = window.setTimeout(() => {
        setIsSceneStarted(true);

        playSceneAudio(step, index);

        sceneStartTimerRef.current = null;
      }, scene.startDelayMs);

      return;
    }

    const shouldDelay =
      scene.type === 'SENTENCE' && scene.activity === 'LISTEN';

    if (!shouldDelay) {
      setIsSceneStarted(true);
      playSceneAudio(step, index);
      return;
    }

    setIsSceneStarted(false);

    sceneStartTimerRef.current = window.setTimeout(() => {
      setIsSceneStarted(true);
      playSceneAudio(step, index);

      sceneStartTimerRef.current = null;
    }, 1000);
  };

  useEffect(() => {
    return () => {
      clearSceneStartTimer();
      clearPointRepeatPhaseTimer();
    };
  }, []);

  const handleChangeLessonStep = (step: number) => {
    if (step > maxUnlockedStep) return;

    setIsLessonCompleted(false);

    setLessonStep(step);
    setSceneIndex(0);

    startScene(step, 0);
  };

  useEffect(() => {
    setIsAudioProgressCompleted(false);

    if (isAudioPlaying || audioProgress < 100) return;

    const timer = window.setTimeout(() => {
      setIsAudioProgressCompleted(true);
    }, 300);

    return () => {
      window.clearTimeout(timer);
    };
  }, [lessonStep, sceneIndex, audioProgress, isAudioPlaying]);

  useEffect(() => {
    if (isLessonCompleted) {
      return;
    }

    const scene = LESSON_SCENE_MAP[lessonStep]?.[sceneIndex];

    if (!scene) {
      setActivityProgress(0);
      setIsActivityCompleted(false);
      return;
    }

    const isTalk = scene.type === 'SENTENCE' && scene.activity === 'TALK';

    const isStorytelling =
      scene.type === 'STORY' && scene.mode === 'STORYTELLING';

    if (!isTalk && !isStorytelling) {
      setActivityProgress(0);
      setIsActivityCompleted(false);
      return;
    }

    const durationSeconds = scene.durationSeconds ?? (isStorytelling ? 20 : 15);

    const durationMs = durationSeconds * 1000;
    const startedAt = Date.now();

    setActivityProgress(0);
    setIsActivityCompleted(false);

    const timer = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const progress = Math.min((elapsed / durationMs) * 100, 100);

      setActivityProgress(progress);

      if (progress >= 100) {
        setActivityProgress(100);
        window.clearInterval(timer);

        window.setTimeout(() => {
          setIsActivityCompleted(true);
        }, 300);
      }
    }, 100);

    return () => {
      window.clearInterval(timer);
    };
  }, [lessonStep, sceneIndex, activityResetKey, isLessonCompleted]);

  useEffect(() => {
    const scene = LESSON_SCENE_MAP[lessonStep]?.[sceneIndex];

    if (
      !scene ||
      scene.type !== 'SENTENCE' ||
      scene.activity !== 'READ_REPEAT' ||
      readRepeatPhase !== 'LISTEN' ||
      !isAudioProgressCompleted
    ) {
      return;
    }

    setReadRepeatPhase('REPEAT');
    setActivityProgress(0);
    setIsActivityCompleted(false);
  }, [lessonStep, sceneIndex, readRepeatPhase, isAudioProgressCompleted]);

  useEffect(() => {
    const scene = LESSON_SCENE_MAP[lessonStep]?.[sceneIndex];

    if (
      !scene ||
      scene.type !== 'SENTENCE' ||
      scene.activity !== 'READ_REPEAT' ||
      readRepeatPhase !== 'REPEAT'
    ) {
      return;
    }

    const durationMs = (scene.durationSeconds ?? 3) * 1000;
    const startedAt = Date.now();

    setActivityProgress(0);

    const timer = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const progress = Math.min((elapsed / durationMs) * 100, 100);

      setActivityProgress(progress);

      if (progress < 100) return;

      window.clearInterval(timer);

      const nextIndex = readRepeatIndex + 1;
      const nextAudio = scene.audios?.[nextIndex];

      if (nextAudio) {
        // 다음 chunk
        setReadRepeatIndex(nextIndex);
        setReadRepeatPhase('LISTEN');
        setActivityProgress(0);
        setIsAudioProgressCompleted(false);

        playAudio(nextAudio);

        return;
      }

      // 모든 chunk 완료
      setActivityProgress(100);

      window.setTimeout(() => {
        setReadRepeatPhase('DONE');
        setIsActivityCompleted(true);
      }, 300);
    }, 100);

    return () => {
      window.clearInterval(timer);
    };
  }, [lessonStep, sceneIndex, readRepeatIndex, readRepeatPhase]);

  useEffect(() => {
    const scene = LESSON_SCENE_MAP[lessonStep]?.[sceneIndex];

    if (
      !scene ||
      scene.type !== 'SENTENCE' ||
      scene.activity !== 'LISTEN_REPEAT' ||
      listenRepeatPhase !== 'LISTEN' ||
      !isAudioProgressCompleted
    ) {
      return;
    }

    setListenRepeatPhase('REPEAT');
    setActivityProgress(0);
    setIsActivityCompleted(false);
  }, [lessonStep, sceneIndex, listenRepeatPhase, isAudioProgressCompleted]);

  useEffect(() => {
    const scene = LESSON_SCENE_MAP[lessonStep]?.[sceneIndex];

    if (
      !scene ||
      scene.type !== 'SENTENCE' ||
      scene.activity !== 'LISTEN_REPEAT' ||
      listenRepeatPhase !== 'REPEAT'
    ) {
      return;
    }

    const durationMs = (scene.durationSeconds ?? 3) * 1000;
    const startedAt = Date.now();

    setActivityProgress(0);

    const timer = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const progress = Math.min((elapsed / durationMs) * 100, 100);

      setActivityProgress(progress);

      if (progress < 100) return;

      window.clearInterval(timer);

      const nextIndex = listenRepeatIndex + 1;
      const nextAudio = scene.audios?.[nextIndex];

      if (nextAudio) {
        // 현재 chunk 완료 → 다음 chunk
        setListenRepeatIndex(nextIndex);
        setListenRepeatPhase('LISTEN');

        setActivityProgress(0);
        setIsAudioProgressCompleted(false);

        playAudio(nextAudio);

        return;
      }

      // 모든 chunk 완료
      setActivityProgress(100);

      window.setTimeout(() => {
        setListenRepeatPhase('DONE');
        setIsActivityCompleted(true);
      }, 300);
    }, 100);

    return () => {
      window.clearInterval(timer);
    };
  }, [lessonStep, sceneIndex, listenRepeatIndex, listenRepeatPhase]);

  useEffect(() => {
    const scene = LESSON_SCENE_MAP[lessonStep]?.[sceneIndex];

    if (!scene || scene.type !== 'SENTENCE' || scene.activity !== 'MEANING') {
      setMeaningStep(0);
      return;
    }

    const meaningGroups =
      scene.meaningGroups ??
      (scene.meaningItems?.length ? [scene.meaningItems] : []);

    const totalChunks = meaningGroups.flat().length;

    setMeaningStep(0);

    const timers = Array.from({ length: totalChunks }, (_, index) =>
      window.setTimeout(
        () => {
          setMeaningStep(index + 1);
        },
        1000 + index * 3000,
      ),
    );

    const completeTimer = window.setTimeout(
      () => {
        setMeaningStep(totalChunks + 1);
      },
      1000 + totalChunks * 3000,
    );

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      window.clearTimeout(completeTimer);
    };
  }, [lessonStep, sceneIndex, activityResetKey]);

  useEffect(() => {
    const scene = LESSON_SCENE_MAP[lessonStep]?.[sceneIndex];

    if (
      !scene ||
      scene.type !== 'SENTENCE' ||
      scene.activity !== 'POINT_REPEAT' ||
      pointRepeatStep !== 0 ||
      !isPointRepeatPhaseStarted ||
      !isAudioProgressCompleted
    ) {
      return;
    }

    setIsAudioProgressCompleted(false);
    setActivityProgress(0);

    // 현재 point 음원 끝
    // → 엄마 설명
    setPointRepeatStep(1);
  }, [
    lessonStep,
    sceneIndex,
    pointRepeatStep,
    isPointRepeatPhaseStarted,
    isAudioProgressCompleted,
  ]);

  useEffect(() => {
    const scene = LESSON_SCENE_MAP[lessonStep]?.[sceneIndex];

    if (
      !scene ||
      scene.type !== 'SENTENCE' ||
      scene.activity !== 'POINT_REPEAT' ||
      pointRepeatStep !== 1
    ) {
      return;
    }

    const part = scene.pointRepeatParts?.[pointRepeatPartIndex];

    if (!part) return;

    const durationMs = 3000;
    const startedAt = Date.now();

    setActivityProgress(0);

    const timer = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;

      const progress = Math.min((elapsed / durationMs) * 100, 100);

      setActivityProgress(progress);

      if (progress < 100) return;

      window.clearInterval(timer);

      const nextPointIndex = pointRepeatIndex + 1;
      const nextPoint = part.points[nextPointIndex];

      setIsAudioProgressCompleted(false);
      setActivityProgress(0);

      if (nextPoint) {
        // God 설명 완료
        // → finished

        // finished 설명 완료
        // → his work
        setPointRepeatIndex(nextPointIndex);
        setPointRepeatStep(0);

        playAudio(nextPoint.audio);

        return;
      }

      // 현재 part의 모든 point를 끝냈음
      // → 전체 chunk 듣기
      setPointRepeatStep(2);

      playAudio(part.fullAudio);
    }, 100);

    return () => {
      window.clearInterval(timer);
    };
  }, [
    lessonStep,
    sceneIndex,
    pointRepeatStep,
    pointRepeatPartIndex,
    pointRepeatIndex,
  ]);

  useEffect(() => {
    const scene = LESSON_SCENE_MAP[lessonStep]?.[sceneIndex];

    if (
      !scene ||
      scene.type !== 'SENTENCE' ||
      scene.activity !== 'POINT_REPEAT' ||
      pointRepeatStep !== 2 ||
      !isAudioProgressCompleted
    ) {
      return;
    }

    const parts = scene.pointRepeatParts ?? [];

    const nextPartIndex = pointRepeatPartIndex + 1;
    const nextPart = parts[nextPartIndex];

    setIsAudioProgressCompleted(false);

    if (nextPart) {
      // 1부분 완료
      // → 2부분 첫 point로
      setPointRepeatPartIndex(nextPartIndex);
      setPointRepeatIndex(0);
      setPointRepeatStep(0);
      setActivityProgress(0);

      const firstPoint = nextPart.points[0];

      if (firstPoint) {
        playAudio(firstPoint.audio);
      }

      return;
    }

    // 모든 부분 완료
    setPointRepeatStep(3);
  }, [
    lessonStep,
    sceneIndex,
    pointRepeatStep,
    pointRepeatPartIndex,
    isAudioProgressCompleted,
  ]);

  useEffect(() => {
    const scene = LESSON_SCENE_MAP[lessonStep]?.[sceneIndex];

    if (
      !scene ||
      scene.type !== 'SENTENCE' ||
      scene.activity !== 'POINT_REPEAT' ||
      pointRepeatStep !== 4 ||
      !isPointRepeatPhaseStarted ||
      !isAudioProgressCompleted
    ) {
      return;
    }

    setPointRepeatStep(5);
  }, [
    lessonStep,
    sceneIndex,
    pointRepeatStep,
    isPointRepeatPhaseStarted,
    isAudioProgressCompleted,
  ]);

  useEffect(() => {
    const scene = LESSON_SCENE_MAP[lessonStep]?.[sceneIndex];

    if (
      !scene ||
      scene.type !== 'SENTENCE' ||
      scene.activity !== 'POINT_REPEAT' ||
      pointRepeatStep !== 5
    ) {
      return;
    }

    const durationMs = (scene.durationSeconds ?? 3) * 1000;
    const startedAt = Date.now();

    setActivityProgress(0);
    setIsActivityCompleted(false);

    const timer = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const progress = Math.min((elapsed / durationMs) * 100, 100);

      setActivityProgress(progress);

      if (progress >= 100) {
        setActivityProgress(100);
        window.clearInterval(timer);

        window.setTimeout(() => {
          const parts = scene.pointRepeatParts ?? [];

          const nextPartIndex = pointRepeatPartIndex + 1;
          const nextPart = parts[nextPartIndex];

          if (nextPart) {
            setPointRepeatPartIndex(nextPartIndex);
            setPointRepeatIndex(0);

            setPointRepeatStep(4);

            setActivityProgress(0);
            setIsActivityCompleted(false);
            setIsAudioProgressCompleted(false);

            playAudio(nextPart.fullAudio);

            return;
          }

          setIsActivityCompleted(true);
          setPointRepeatStep(6);
        }, 300);
      }
    }, 100);

    return () => {
      window.clearInterval(timer);
    };
  }, [lessonStep, sceneIndex, pointRepeatStep, pointRepeatPartIndex]);

  useEffect(() => {
    const scene = LESSON_SCENE_MAP[lessonStep]?.[sceneIndex];

    const isStorytelling =
      scene?.type === 'STORY' && scene.mode === 'STORYTELLING';

    if (isStorytelling) return;

    setIsStoryHintVisible(false);
    setStoryHintIndex(0);
  }, [lessonStep, sceneIndex]);

  const linkToken = sessionStorage.getItem('freeTrialLinkToken');

  useEffect(() => {
    if (!linkToken) return;

    const reportLessonProgress = async () => {
      try {
        await reportFreeTrialStepProgress({
          linkToken,
          step: 'lesson',
        });
      } catch (error) {
        dispatch(openAlert({ message: getErrorMessages(error) }));
        console.error('수업 진행 상태 저장 실패', error);
      }
    };

    void reportLessonProgress();
  }, [linkToken]);

  const handleStartReview = () => {
    onComplete();
  };

  const handleStart = () => {
    const nextSceneIndex = 1;

    setSceneIndex(nextSceneIndex);
    startScene(0, nextSceneIndex);
  };

  const handleLessonPrev = () => {
    const scene = LESSON_SCENE_MAP[lessonStep]?.[sceneIndex];

    // 6-2 → 6-1
    if (
      scene?.type === 'SENTENCE' &&
      scene.activity === 'POINT_REPEAT' &&
      pointRepeatStep >= 4
    ) {
      startPointRepeatPhase(0, lessonStep, sceneIndex);
      return;
    }

    if (sceneIndex > 0) {
      const prevSceneIndex = sceneIndex - 1;

      setSceneIndex(prevSceneIndex);
      startScene(lessonStep, prevSceneIndex);

      return;
    }

    if (lessonStep === 0) return;

    const prevStep = lessonStep - 1;
    const prevScenes = LESSON_SCENE_MAP[prevStep] ?? [];
    const prevSceneIndex = Math.max(prevScenes.length - 1, 0);

    setLessonStep(prevStep);
    setSceneIndex(prevSceneIndex);

    startScene(prevStep, prevSceneIndex);
  };

  const handleLessonNext = () => {
    const scene = LESSON_SCENE_MAP[lessonStep]?.[sceneIndex];

    if (scene?.type === 'SENTENCE' && scene.activity === 'POINT_REPEAT') {
      // 6-1 → 6-2
      if (pointRepeatStep < 4) {
        startPointRepeatPhase(4, lessonStep, sceneIndex);
        return;
      }
    }

    const scenes = LESSON_SCENE_MAP[lessonStep] ?? [];

    if (sceneIndex < scenes.length - 1) {
      const nextSceneIndex = sceneIndex + 1;

      setSceneIndex(nextSceneIndex);
      startScene(lessonStep, nextSceneIndex);

      return;
    }

    if (lessonStep < LESSON_STEPS.length - 1) {
      const nextStep = lessonStep + 1;

      setLessonStep(nextStep);
      setSceneIndex(0);
      setMaxUnlockedStep((prev) => Math.max(prev, nextStep));

      startScene(nextStep, 0);

      return;
    }

    if (scene?.type === 'STORY' && scene.mode === 'STORYTELLING') {
      stopAudio();
      setIsLessonCompleted(true);

      return;
    }
  };

  const handleReplay = () => {
    const scene = LESSON_SCENE_MAP[lessonStep]?.[sceneIndex];

    if (!scene) return;

    if (scene.type === 'SENTENCE' && scene.activity === 'POINT_REPEAT') {
      const startStep = pointRepeatStep >= 4 ? 4 : 0;

      startPointRepeatPhase(startStep, lessonStep, sceneIndex);
      return;
    }

    if (scene.type === 'STORY' && scene.mode === 'STORYTELLING') {
      stopAudio();

      setActivityResetKey((prev) => prev + 1);

      return;
    }

    if (
      scene.type === 'SENTENCE' &&
      (scene.activity === 'TALK' || scene.activity === 'MEANING')
    ) {
      stopAudio();
      setActivityResetKey((prev) => prev + 1);
      return;
    }

    if (
      scene.type === 'SENTENCE' &&
      ['LISTEN', 'READ_REPEAT', 'LISTEN_REPEAT'].includes(scene.activity)
    ) {
      startScene(lessonStep, sceneIndex);
      return;
    }

    stopAudio();
    playSceneAudio(lessonStep, sceneIndex);
  };

  const handleToggleStoryHint = () => {
    setIsStoryHintVisible((prev) => !prev);
  };

  const handleNextStoryHint = () => {
    setStoryHintIndex((prev) =>
      Math.min(prev + 1, STORYTELLING_HINT_POINTS.length - 1),
    );
  };

  const handleRestartStoryHint = () => {
    setStoryHintIndex(0);
  };

  return (
    <section className="mt-5 p-0 md:rounded-2xl md:bg-white md:px-6 md:py-7 md:shadow-[0_6px_20px_rgba(40,25,100,0.05)]">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Typography className="text-[11px] font-extrabold text-[#6737F4] md:text-[12px]">
            STEP 1 · 엄마와 함께 · 약 15분
          </Typography>

          <Typography className="mt-1 text-[22px] font-black leading-[1.3] text-gray-900 md:text-[26px]">
            수업자료로 함께 수업해요
          </Typography>
        </div>

        <button
          type="button"
          onClick={onShowTutorial}
          className="w-fit shrink-0 rounded-full bg-[#F4F0FF] px-3 py-1.5 text-[10px] font-bold text-[#6737F4] transition hover:bg-[#EDE7FF] md:text-[11px]"
        >
          시작 전 안내 다시 보기
        </button>
      </div>

      <div className="mt-2 md:mt-6 md:rounded-2xl md:bg-[#F5F2FF] md:p-4">
        <LessonHeader
          currentStep={isLessonCompleted ? null : lessonStep}
          maxUnlockedStep={maxUnlockedStep}
          onChangeStep={handleChangeLessonStep}
        />

        <div className="mt-3 grid items-start gap-2 lg:grid-cols-[minmax(0,1.55fr)_minmax(280px,0.95fr)]">
          {isLessonCompleted ? (
            <>
              <LessonCompleteContent />

              <LessonCompleteGuide
                isReporting={isReporting}
                onComplete={handleStartReview}
              />
            </>
          ) : (
            <>
              <FreeTrialLessonContent
                currentStep={lessonStep}
                sceneIndex={sceneIndex}
                activityResetKey={activityResetKey}
                audioProgress={audioProgress}
                isAudioPlaying={isAudioPlaying}
                activityProgress={activityProgress}
                isActivityCompleted={isActivityCompleted}
                meaningStep={meaningStep}
                pointRepeatStep={pointRepeatStep}
                pointRepeatPartIndex={pointRepeatPartIndex}
                pointRepeatIndex={pointRepeatIndex}
                readRepeatIndex={readRepeatIndex}
                readRepeatPhase={readRepeatPhase}
                listenRepeatIndex={listenRepeatIndex}
                listenRepeatPhase={listenRepeatPhase}
                isStoryHintVisible={isStoryHintVisible}
                storyHintIndex={storyHintIndex}
                isSceneStarted={isSceneStarted}
                isPointRepeatPhaseStarted={isPointRepeatPhaseStarted}
                onStart={handleStart}
              />

              <FreeTrialLessonGuide
                isReporting={isReporting}
                currentStep={lessonStep}
                sceneIndex={sceneIndex}
                audioProgress={audioProgress}
                isAudioPlaying={isAudioPlaying}
                isAudioProgressCompleted={isAudioProgressCompleted}
                activityProgress={activityProgress}
                isActivityCompleted={isActivityCompleted}
                meaningStep={meaningStep}
                pointRepeatStep={pointRepeatStep}
                pointRepeatPartIndex={pointRepeatPartIndex}
                pointRepeatIndex={pointRepeatIndex}
                readRepeatPhase={readRepeatPhase}
                isStoryHintVisible={isStoryHintVisible}
                storyHintIndex={storyHintIndex}
                isSceneStarted={isSceneStarted}
                isPointRepeatPhaseStarted={isPointRepeatPhaseStarted}
                onPrev={handleLessonPrev}
                onNext={handleLessonNext}
                onReplay={handleReplay}
                onToggleStoryHint={handleToggleStoryHint}
                onNextStoryHint={handleNextStoryHint}
                onRestartStoryHint={handleRestartStoryHint}
              />
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default FreeTrialLessonView;

interface LessonHeaderProps {
  currentStep: number | null;
  maxUnlockedStep: number;
  onChangeStep: (step: number) => void;
}

const LessonHeader = ({
  currentStep,
  maxUnlockedStep,
  onChangeStep,
}: LessonHeaderProps) => {
  return (
    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      <Typography className="text-[11px] font-bold text-[#4D2CD0] md:text-[12px]">
        English Story Bible · Book 1 · Lesson 2{' '}
        <span className="ml-1 text-[16px] font-black text-gray-900 md:text-[18px]">
          In the <span className="text-red-500">beginning</span>
        </span>
      </Typography>

      <div className="flex max-w-full gap-1.5 overflow-x-auto pb-1 lg:pb-0">
        {LESSON_STEPS.map((item, index) => {
          const isActive = index === currentStep;
          const isLocked = index > maxUnlockedStep;

          return (
            <button
              key={item}
              type="button"
              disabled={isLocked}
              onClick={() => onChangeStep(index)}
              className={`shrink-0 rounded-full border px-3 py-1.5 text-[9px] font-bold transition md:text-[10px] ${
                isActive
                  ? 'border-[#6737F4] bg-[#6737F4] text-white'
                  : isLocked
                    ? 'cursor-default border-gray-200 bg-gray-50 text-gray-300'
                    : 'border-[#DED8F2] bg-white text-gray-700 hover:border-[#BDAEFF]'
              }`}
            >
              {item}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const LessonCompleteContent = () => {
  return (
    <div className="flex min-h-[320px] flex-col md:min-h-[430px]">
      <div className="flex flex-1 flex-col items-center justify-center rounded-2xl border border-[#DED8F2] bg-white px-5 py-10 text-center">
        <Typography className="text-[16px] font-bold text-gray-700 md:text-[18px]">
          Great job!
        </Typography>

        <Typography className="mt-2 text-[34px] font-black leading-tight text-gray-900 md:text-[42px]">
          참 잘했어요!
        </Typography>

        <Typography className="mt-3 text-[20px] font-black text-[#6737F4] md:text-[22px]">
          그림만 보고 세 문장을 말했어요
        </Typography>
      </div>

      <div className="mt-2 flex min-h-[35px] items-center justify-center rounded-xl border border-[#DED8F2] bg-white px-4">
        <Typography className="text-[10px] font-medium text-gray-700 md:text-[11px]">
          다음 단계: 온라인 복습 → AI 인증제 → 뱃지
        </Typography>
      </div>
    </div>
  );
};

interface LessonCompleteGuideProps {
  isReporting: boolean;
  onComplete: () => void;
}

const LessonCompleteGuide = ({
  isReporting,
  onComplete,
}: LessonCompleteGuideProps) => {
  return (
    <div className="flex flex-col rounded-2xl border border-[#DDD5F5] bg-white p-3 md:p-4">
      <Typography className="text-[10px] font-extrabold text-[#6737F4]">
        수업 마무리
      </Typography>

      <Typography className="mt-3 hidden text-[20px] font-black text-gray-900 md:block">
        수업을 마쳤어요
      </Typography>

      <div className="mt-2 rounded-xl bg-[#EEE9FF] px-3 py-2 md:mt-4 md:px-4 md:py-2.5">
        <span className="inline-flex rounded-full bg-[#6737F4] px-2.5 py-1 text-[9px] font-bold text-white">
          엄마 가이드
        </span>

        <Typography className="mt-2 text-[12px] font-extrabold leading-5 text-gray-900 md:text-[13px]">
          “그림만 보고 세 문장을 말했어요! 이제 혼자 복습하고, AI 선생님에게
          확인받아 볼까요?”
        </Typography>

        <Typography className="mt-3 hidden border-t border-dashed border-[#CFC4FF] pt-2 text-[11px] leading-5 text-gray-700 md:block">
          실제 수업에서 엄마·선생님이 안내하는 말을 ‘엄마 가이드’로 담았어요.
        </Typography>
      </div>

      <Typography className="mt-2 text-[10px] leading-4 text-gray-700 md:mt-4 md:text-[11px] md:leading-5">
        ‘수업 완료!’를 누르면 온라인 복습이 열리고, 이어서 AI 인증제에 도전할 수
        있어요.
      </Typography>

      <div className="relative mt-3 w-full rounded-xl pb-[4px] md:mt-4">
        {!isReporting && <FreeTrialAttentionEffect />}

        <button
          type="button"
          onClick={onComplete}
          disabled={isReporting}
          className="relative z-10 flex h-12 w-full items-center justify-center rounded-xl bg-[#6737F4] text-[14px] font-black text-white shadow-[0_4px_0_#4D24C7] transition hover:bg-[#5425D6] disabled:bg-[#CFC4F5] disabled:shadow-none"
        >
          {isReporting ? '처리 중...' : '수업 완료!'}
        </button>
      </div>
    </div>
  );
};
